import { Provider } from '../../../models/Entities/Provider/Provider';
import { PersonTypeEnum } from '../../../models/Entities/Person/PersonCommands';

/*
 * Lógica pura de la lista de proveedores: nombre a mostrar, identificador,
 * tipo de persona y filtro de búsqueda. Sin React ni store, para poder testearla sola.
 */

/** Razón social, o un respaldo con el id si el proveedor vino sin persona. */
export const providerName = (provider: Pick<Provider, 'id' | 'person'>): string => {
    const name = (provider.person?.businessName || '').trim();
    return name || `Proveedor #${provider.id}`;
};

/** Identificador de la persona (CUIT/RUT...), vacío si no tiene. */
export const providerIdentifier = (provider: Pick<Provider, 'person'>): string => (provider.person?.personIdentifier || '').trim();

export type ProviderPersonKind = 'natural' | 'juridical' | undefined;

export const providerPersonKind = (provider: Pick<Provider, 'person'>): ProviderPersonKind => {
    switch (provider.person?.personType) {
        case PersonTypeEnum.NaturalPerson: return 'natural';
        case PersonTypeEnum.JuridicalPerson: return 'juridical';
        default: return undefined;
    }
};

/** Iniciales para el avatar: hasta dos letras de las dos primeras palabras ("Laboratorio Central" → "LC"). */
export const providerInitials = (provider: Pick<Provider, 'id' | 'person'>): string => {
    const words = providerName(provider).split(/\s+/).filter((word) => /[\p{L}\p{N}]/u.test(word));
    const letters = words.slice(0, 2).map((word) => word.replace(/[^\p{L}\p{N}]/gu, '').charAt(0));
    return letters.join('').toUpperCase() || '#';
};

/** Minúsculas y sin acentos, para que "laboratorio" encuentre "Laboratório". */
const fold = (text: string): string => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export const matchesSearch = (provider: Pick<Provider, 'id' | 'person'>, search: string | undefined): boolean => {
    const query = fold((search || '').trim());
    if (!query) return true;
    return fold(providerName(provider)).includes(query)
        || fold(providerIdentifier(provider)).includes(query)
        || String(provider.id) === query;
};

export const filterProviders = (providers: Provider[] | null | undefined, search: string | undefined): Provider[] =>
    (providers || []).filter((provider) => matchesSearch(provider, search));
